import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { identify as analyticsIdentify } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ensure axios always sends cookies for cross-origin/preview calls.
axios.defaults.withCredentials = true;

// ── Bearer-token fallback (Safari/iPad ITP) ───────────────────────
// Some browsers (notably iPad Safari) silently drop our auth cookies on
// follow-up XHRs even when the cookie was set on a same-origin POST.
// We mirror the access_token into an Authorization: Bearer header so the
// backend's get_optional_user / get_current_user_from_db dependency
// (which already accepts either cookie OR Bearer) keeps working.
const TOKEN_KEY = 'sow_access_token';

const setBearer = (token) => {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* private mode */ }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Hydrate Authorization header from localStorage on first load so a
// returning user keeps working even if the cookie was evicted by ITP.
try {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored) axios.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
} catch { /* private mode — ignore */ }

const AuthContext = createContext(null);

export const formatApiError = (e) => {
  const d = e?.response?.data;
  if (!d) return e?.message || 'Something went wrong. Please try again.';
  if (typeof d === 'string') return d;
  if (typeof d.error === 'string') return d.error;
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) return d.detail.map(x => x?.msg || JSON.stringify(x)).join(' ');
  return e?.message || 'Something went wrong.';
};

export const AuthProvider = ({ children }) => {
  // null = checking, object = authed, false = not authed
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch (err) {
      // Not authenticated is the expected path; log anything else
      if (err?.response?.status && err.response.status !== 401) {
        console.error('Auth refresh failed:', err?.response?.status, err?.message);
      }
      // Clear any stale Bearer token on 401 so we don't keep sending a dead JWT
      if (err?.response?.status === 401) setBearer(null);
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const register = async ({ email, password, marketing_consent, terms_accepted, indicative_project_id }) => {
    const res = await axios.post(`${API}/auth/register`, {
      email, password, marketing_consent, terms_accepted, indicative_project_id,
    });
    if (res.data?.access_token) setBearer(res.data.access_token);
    setUser(res.data.user);
    analyticsIdentify(res.data.user?.user_id);
    return res.data.user;
  };

  const login = async ({ email, password }) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data?.access_token) setBearer(res.data.access_token);
    setUser(res.data.user);
    analyticsIdentify(res.data.user?.user_id);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Logout failure shouldn't block the local sign-out, but log for visibility
      console.error('Logout API call failed:', err?.message);
    }
    setBearer(null);
    setUser(false);
  };

  const forgotPassword = async (email) => {
    await axios.post(`${API}/auth/forgot-password`, { email });
    // Always succeeds — message is generic for security
  };

  const resetPassword = async ({ token, password }) => {
    const res = await axios.post(`${API}/auth/reset-password`, { token, password });
    if (res.data?.access_token) setBearer(res.data.access_token);
    if (res.data.user) setUser(res.data.user);
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh, register, login, logout, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
